import { useCallback, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { MedicalRecordInput } from "@/lib/validations/medical-record";
import i18n from "@/i18n";
import { AI_PROCESSING_TIMEOUT_MS } from "@/components/medical-records/transcription/constants";
import type { AiStatus } from "@/components/medical-records/transcription/transcription-status";

// Types
export type DiagnosisSuggestion = {
  label: string;
  confidence: number;
  reasoning?: string;
  recommended_tests?: string;
};

export type UnknownTerm = {
  term: string;
  context?: string;
  suggestion?: string;
};

interface TranscriptionResponse {
  transcript: string;
  structured: Partial<MedicalRecordInput>;
  summary?: string;
  tokens?: number;
  diagnosisSuggestions?: DiagnosisSuggestion[];
  unknownTerms?: UnknownTerm[];
  detectedLanguage?: string;
  warning?: string;
}

interface RunAiExtractionOptions {
  metadata?: Record<string, unknown>;
}

const TRANSCRIPT_LOG_PREVIEW_LENGTH = 200;

const truncateTranscriptForLog = (value: string, maxLength = TRANSCRIPT_LOG_PREVIEW_LENGTH) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}...[truncated]` : value;

const buildTranscriptionLogPayload = (
  event: string,
  transcriptionId: string,
  extra?: Record<string, unknown>,
) => ({
  event,
  transcriptionId,
  ts: new Date().toISOString(),
  tsMs: Date.now(),
  ...extra,
});

interface TranscriptionState {
  status: AiStatus;
  transcript: string;
  structured: Partial<MedicalRecordInput>;
  summary?: string;
  diagnosisSuggestions: DiagnosisSuggestion[];
  unknownTerms: UnknownTerm[];
  detectedLanguage?: string;
  warning?: string;
  error: string | null;
}

interface LoadFromSessionInput {
  transcript: string;
  structured: Record<string, unknown> | null;
  summary?: string | null;
  diagnosis_suggestions: unknown;
  unknown_terms: unknown;
  detected_language?: string;
  ai_status: string;
  ai_last_error?: string | null;
}

interface UseConsultationTranscriptionResult extends TranscriptionState {
  isProcessing: boolean;
  reset: () => void;
  loadFromSession: (sessionData: LoadFromSessionInput) => void;
  runAiExtraction: (transcriptionId: string, leaseToken: string, options?: RunAiExtractionOptions) => Promise<void>;
  /** Marca a extracao como falha para destravar a UI (usado pelo watchdog de "analisando"). */
  markAiFailed: () => void;
}

// Constants
const INITIAL_STATE: TranscriptionState = {
  status: "idle",
  transcript: "",
  structured: {},
  summary: undefined,
  diagnosisSuggestions: [],
  unknownTerms: [],
  detectedLanguage: undefined,
  warning: undefined,
  error: null,
};

/** Erro interno do fluxo: nunca chega ao usuario, so ao console. */
class EdgeFunctionError extends Error {
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = "EdgeFunctionError";
    this.code = code;
  }
}

/**
 * O supabase-js entrega o corpo da resposta de erro em `context` (um Response).
 * O `code` que a edge function devolve distingue corrida benigna de falha real.
 */
const readEdgeFunctionErrorCode = async (functionError: unknown): Promise<string | undefined> => {
  const context = (functionError as { context?: unknown })?.context;
  if (!context || typeof (context as Response).json !== "function") return undefined;

  try {
    const body = await (context as Response).clone().json();
    const code = (body as { code?: unknown })?.code;
    return typeof code === "string" ? code : undefined;
  } catch {
    return undefined;
  }
};

const isAiStatus = (value: string): value is AiStatus =>
  value === "idle" || value === "processing" || value === "completed" || value === "error";

/**
 * Hook for managing consultation transcription and AI processing.
 * A captura do transcript nao passa mais por aqui: este hook so cuida do
 * ciclo de vida da extracao por IA, lida atraves de `ai_status`.
 */
export const useConsultationTranscription = (): UseConsultationTranscriptionResult => {
  const [state, setState] = useState<TranscriptionState>(INITIAL_STATE);
  const inFlightExtractionRef = useRef(false);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const updateStateFromResponse = useCallback((data: TranscriptionResponse) => {
    setState((prev) => ({
      ...prev,
      status: "completed",
      transcript: data.transcript,
      structured: data.structured ?? {},
      summary: data.summary,
      diagnosisSuggestions: data.diagnosisSuggestions ?? [],
      unknownTerms: data.unknownTerms ?? [],
      detectedLanguage: data.detectedLanguage,
      warning: data.warning,
      error: null,
    }));
  }, []);

  const loadFromSession = useCallback((sessionData: LoadFromSessionInput) => {
    const nextStatus: AiStatus = isAiStatus(sessionData.ai_status) ? sessionData.ai_status : "idle";

    const newState: TranscriptionState = {
      status: nextStatus,
      transcript: sessionData.transcript || "",
      structured: sessionData.structured || {},
      summary: sessionData.summary || undefined,
      diagnosisSuggestions: Array.isArray(sessionData.diagnosis_suggestions) ? sessionData.diagnosis_suggestions : [],
      unknownTerms: Array.isArray(sessionData.unknown_terms) ? sessionData.unknown_terms : [],
      detectedLanguage: sessionData.detected_language,
      warning: undefined,
      error: nextStatus === "error" ? sessionData.ai_last_error ?? null : null,
    };

    setState(newState);
  }, []);

  const setError = useCallback((errorMessage: string) => {
    setState((prev) => ({
      ...prev,
      status: "error",
      error: errorMessage,
    }));
  }, []);

  const setProcessing = useCallback(() => {
    setState((prev) => ({
      ...prev,
      status: "processing",
      error: null,
    }));
  }, []);

  /**
   * Roda a extracao por IA sobre um transcript ja finalizado no banco.
   * A edge function le o transcript persistido — nada e enviado aqui alem do lease.
   */
  const runAiExtraction = useCallback(
    async (transcriptionId: string, leaseToken: string, options?: RunAiExtractionOptions): Promise<void> => {
      // Uma extracao em voo por vez: chamadas concorrentes disputariam o lease e a
      // perdedora receberia "already_processing" sem que nada estivesse errado.
      if (inFlightExtractionRef.current) {
        console.log("[transcription] runAiExtraction:skippedAlreadyInFlight", { transcriptionId });
        return;
      }
      inFlightExtractionRef.current = true;

      try {
        setProcessing();

        const resolvedMetadata: Record<string, unknown> = {
          ...(options?.metadata ?? {}),
          current_datetime: new Date().toISOString(),
        };
        if (typeof resolvedMetadata["timezone"] !== "string" || !resolvedMetadata["timezone"]) {
          resolvedMetadata["timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
        }

        console.log(
          "[transcription] runAiExtraction:start",
          buildTranscriptionLogPayload("run_ai_extraction_start", transcriptionId),
        );

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), AI_PROCESSING_TIMEOUT_MS);

        let data: TranscriptionResponse | null = null;
        try {
          const invokeStartedAt = Date.now();
          const response = await supabase.functions.invoke<TranscriptionResponse>("transcribe-consultation", {
            body: {
              transcription_id: transcriptionId,
              ai_lease_token: leaseToken,
              metadata: resolvedMetadata,
            },
            signal: controller.signal,
          });

          console.log(
            "[transcription] runAiExtraction:resolved",
            buildTranscriptionLogPayload("run_ai_extraction_resolved", transcriptionId, {
              durationMs: Date.now() - invokeStartedAt,
              hasFunctionError: Boolean(response.error),
              hasData: Boolean(response.data),
            }),
          );

          if (response.error) {
            const code = await readEdgeFunctionErrorCode(response.error);

            // Corrida benigna: o servidor ja esta processando esta transcricao, ou
            // outra tentativa assumiu o lease. Nao e falha — o poll acompanha o
            // resultado. Mostrar erro aqui assustaria o usuario a toa.
            if (code === "already_processing" || code === "lease_lost") {
              console.log("[transcription] runAiExtraction:benignRace", { transcriptionId, code });
              return;
            }

            throw new EdgeFunctionError(response.error.message ?? "edge_function_error", code);
          }

          if (!response.data) {
            throw new EdgeFunctionError("empty_response");
          }

          data = response.data;
        } catch (invokeError) {
          if (controller.signal.aborted) {
            throw new EdgeFunctionError("processing_timeout", "timeout");
          }
          throw invokeError;
        } finally {
          window.clearTimeout(timeoutId);
        }

        updateStateFromResponse(data);
      } catch (processingError) {
        // O detalhe cru (stack, mensagem da edge function, codigo HTTP) fica no console.
        // O usuario ve sempre a mesma frase em portugues — jargao de servidor na tela
        // parece bug e nao ajuda em nada quem esta atendendo.
        console.error("[transcription] runAiExtraction:failed", { transcriptionId, processingError });
        setError(i18n.t("medicalRecords:transcription.aiFailure.description"));
      } finally {
        inFlightExtractionRef.current = false;
      }
    },
    [setError, setProcessing, updateStateFromResponse],
  );

  const markAiFailed = useCallback(() => {
    inFlightExtractionRef.current = false;
    setError(i18n.t("medicalRecords:transcription.aiFailure.description"));
  }, [setError]);

  return {
    ...state,
    isProcessing: state.status === "processing",
    reset,
    loadFromSession,
    runAiExtraction,
    markAiFailed,
  };
};
